/**
 * 企业微信网页授权码登录
 *
 * 流程：
 *   1. 检测在企业微信环境且未登录
 *   2. 检查 URL 是否带有 code 参数（从企业微信授权重定向回来）
 *   3. 如果有 code，调用 /wx/workCodeLogin 完成登录，然后用 location.replace 重新加载干净页面
 *   4. 如果没有 code，用 location.replace 跳转企业微信授权页面
 *
 * 关键：全程使用 location.replace 代替 location.href / history.replaceState，
 * 避免授权流程在浏览器历史记录中产生多余条目，导致手势返回死循环。
 */
import { fetchWorkWxAuthUrl, fetchWorkCodeLogin } from '@/api/common'
import { isWorkWechat } from '@/utils/mobile'

// 防止重复跳转授权
let isAuthRedirecting = false

/**
 * 从当前 URL 中提取企业微信授权码
 * 企业微信授权后会重定向回前端页面，URL 格式：?code=xxx&state=xxx
 */
function getAuthCodeFromUrl(): string | null {
  const searchParams = new URLSearchParams(window.location.search)
  return searchParams.get('code')
}

/**
 * 构建去除 code 和 state 参数后的干净 URL
 * 保留其他查询参数（如 keyword、onlyMine 等）
 */
function getCleanUrl(): string {
  const url = new URL(window.location.href)
  url.searchParams.delete('code')
  url.searchParams.delete('state')
  return url.pathname + url.search + url.hash
}

/**
 * 企业微信网页授权码登录
 * @returns 登录结果；不在企业微信环境或登录失败返回 false
 */
export async function workWxCodeLogin(): Promise<boolean> {
  // 不在企业微信环境，直接返回
  if (!isWorkWechat()) {
    return false
  }

  // 防止重复跳转
  if (isAuthRedirecting) {
    return false
  }

  // 检查 URL 是否带有授权码（从企业微信授权重定向回来）
  const code = getAuthCodeFromUrl()
  if (code) {
    // 有 code，用 code 登录
    try {
      await fetchWorkCodeLogin({ code })
      console.log('[workWxCodeLogin] 授权码登录成功')
      // 登录成功，用 replace 重新加载干净页面，清除授权产生的多余历史记录
      // 这样浏览器历史中只保留当前页面一条记录，手势返回能正常退出
      window.location.replace(getCleanUrl())
      // 页面即将重新加载，下面的代码不会执行
      return true
    } catch (error) {
      console.warn('[workWxCodeLogin] 授权码登录失败:', error)
      return false
    }
  }

  // 没有 code，跳转到企业微信授权页面
  try {
    isAuthRedirecting = true
    const currentUrl = window.location.origin + window.location.pathname + window.location.hash
    const result = await fetchWorkWxAuthUrl({ redirectUrl: currentUrl })
    console.log('[workWxCodeLogin] 跳转企业微信授权页面')
    // 使用 replace 跳转，不保留当前页面在历史记录中
    // 避免返回时又回到未登录状态触发二次授权
    window.location.replace(result.authUrl)
    // 页面即将跳转，下面的代码不会执行
    return false
  } catch (error) {
    console.warn('[workWxCodeLogin] 获取授权链接失败:', error)
    isAuthRedirecting = false
    return false
  }
}
